/**
 * Study Detail Page
 *
 * View study information, history, and manage returned studies
 */

import { useState, useEffect } from 'react'
import { Link, useParams, useNavigate } from 'react-router-dom'
import {
  ArrowLeft,
  FileImage,
  Download,
  Trash2,
  User,
  Calendar,
  Building2,
  Activity,
  HardDrive,
  Clock,
  Loader2,
  AlertCircle,
  Play,
  FileText,
  Edit,
  UserPlus,
  History,
  Upload,
  AlertTriangle,
  Undo2,
  CheckCircle,
  XCircle,
  Eye,
  Wrench,
} from 'lucide-react'

import {
  useStudy,
  useDeleteStudy,
  useDescargarEstudio,
  useUpdateStudy,
  formatStudyDate,
  formatStudyTime,
  formatFileSize,
  STUDY_STATUS_LABELS,
  STUDY_STATUS_COLORS,
} from '@/application/studies'
import { usePatient } from '@/application/patients'
import { useWorklistItemByStudy } from '@/application/worklist'
import {
  useResourceHistory,
  AUDIT_ACTION_LABELS,
  getActionColor,
} from '@/application/audit'
import { useAuth } from '@/application/auth'
import { Button } from '@/presentation/components/ui/Button'
import { Badge } from '@/presentation/components/ui/Badge'
import { Card, CardHeader, CardTitle, CardContent } from '@/presentation/components/ui/Card'
import { Input } from '@/presentation/components/ui/Input'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/presentation/components/ui/Dialog'
// Table imports removed - using compact list view for history
import { AssignStudyModal } from '@/presentation/components/studies'
import { toast } from '@/lib/toast'
import { ConfirmDialog } from '@/presentation/components/ui/ConfirmDialog'
import { MODALITY_LABELS, USER_ROLES, WORKLIST_STATUS_LABELS, type Modality } from '@/config/constants'

// =============================================================================
// Helper Functions
// =============================================================================

function formatDateTime(dateValue: string | Date | null | undefined): string {
  if (!dateValue) return '-'
  try {
    const date = typeof dateValue === 'string' ? new Date(dateValue) : dateValue
    if (isNaN(date.getTime())) return '-'
    return date.toLocaleDateString('es-CO', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    })
  } catch {
    return '-'
  }
}

// =============================================================================
// Helper Components
// =============================================================================

interface InfoItemProps {
  icon: React.ReactNode
  label: string
  value: string | null | undefined
}

function InfoItem({ icon, label, value }: InfoItemProps) {
  return (
    <div className="flex items-start gap-3">
      <div className="p-2 rounded-lg bg-[var(--secondary)] text-[var(--foreground-muted)]">
        {icon}
      </div>
      <div className="flex-1">
        <p className="text-sm text-[var(--foreground-muted)]">{label}</p>
        <p className="font-medium text-[var(--foreground)]">
          {value || <span className="text-[var(--foreground-muted)]">No registrado</span>}
        </p>
      </div>
    </div>
  )
}

function LoadingState() {
  return (
    <div className="flex flex-col items-center justify-center h-[60vh]">
      <Loader2 className="h-8 w-8 animate-spin text-[var(--primary)]" />
      <p className="text-[var(--foreground-muted)] mt-4">Cargando estudio...</p>
    </div>
  )
}

function ErrorState({ message, onRetry }: { message: string; onRetry: () => void }) {
  return (
    <div className="flex flex-col items-center justify-center h-[60vh] text-center">
      <AlertCircle className="h-12 w-12 text-[var(--destructive)] mb-4" />
      <h3 className="text-lg font-semibold text-[var(--foreground)]">Error al cargar</h3>
      <p className="text-[var(--foreground-muted)] mt-1">{message}</p>
      <Button onClick={onRetry} className="mt-4">
        Reintentar
      </Button>
    </div>
  )
}

// =============================================================================
// Edit Study Modal
// =============================================================================

interface EditStudyModalProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  study: {
    id: string
    studyDescription: string | null
    referringPhysician: string | null
    performingPhysician: string | null
    bodyPartExamined: string | null
  }
  onSuccess: () => void
}

function EditStudyModal({ open, onOpenChange, study, onSuccess }: EditStudyModalProps) {
  const [studyDescription, setStudyDescription] = useState(study.studyDescription || '')
  const [referringPhysician, setReferringPhysician] = useState(study.referringPhysician || '')
  const [performingPhysician, setPerformingPhysician] = useState(study.performingPhysician || '')
  const [bodyPartExamined, setBodyPartExamined] = useState(study.bodyPartExamined || '')

  const updateMutation = useUpdateStudy()

  // Reset form when study changes
  useEffect(() => {
    setStudyDescription(study.studyDescription || '')
    setReferringPhysician(study.referringPhysician || '')
    setPerformingPhysician(study.performingPhysician || '')
    setBodyPartExamined(study.bodyPartExamined || '')
  }, [study])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    try {
      await updateMutation.mutateAsync({
        id: study.id,
        data: {
          studyDescription: studyDescription || undefined,
          referringPhysician: referringPhysician || undefined,
          performingPhysician: performingPhysician || undefined,
          bodyPartExamined: bodyPartExamined || undefined,
        },
      })
      toast.success('Estudio actualizado correctamente')
      onOpenChange(false)
      onSuccess()
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Error al actualizar el estudio')
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>Editar Estudio</DialogTitle>
          <DialogDescription>
            Modifica los datos del estudio
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4 mt-4">
          <div>
            <label className="block text-sm font-medium mb-1">Descripción del estudio</label>
            <Input
              value={studyDescription}
              onChange={(e) => setStudyDescription(e.target.value)}
              placeholder="Ej: TC de Tórax con contraste"
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Médico remitente</label>
            <Input
              value={referringPhysician}
              onChange={(e) => setReferringPhysician(e.target.value)}
              placeholder="Nombre del médico"
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Médico ejecutante</label>
            <Input
              value={performingPhysician}
              onChange={(e) => setPerformingPhysician(e.target.value)}
              placeholder="Nombre del médico"
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Parte del cuerpo</label>
            <Input
              value={bodyPartExamined}
              onChange={(e) => setBodyPartExamined(e.target.value)}
              placeholder="Ej: CHEST, HEAD, ABDOMEN"
            />
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancelar
            </Button>
            <Button type="submit" disabled={updateMutation.isPending}>
              {updateMutation.isPending ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                  Guardando...
                </>
              ) : (
                'Guardar cambios'
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}

// =============================================================================
// Replace DICOM Modal
// =============================================================================

interface ReplaceDicomModalProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  studyId: string
  onSuccess: () => void
}

function ReplaceDicomModal({ open, onOpenChange, studyId: _studyId, onSuccess }: ReplaceDicomModalProps) {
  const [file, setFile] = useState<File | null>(null)
  const [isUploading, setIsUploading] = useState(false)

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = e.target.files?.[0]
    if (selectedFile) {
      setFile(selectedFile)
    }
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!file) return

    setIsUploading(true)
    try {
      toast.info('Esta funcionalidad estará disponible próximamente. Por ahora, suba un nuevo estudio y elimine el anterior.')
      onOpenChange(false)
      onSuccess()
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Error al reemplazar el archivo DICOM')
    } finally {
      setIsUploading(false)
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Upload className="h-5 w-5" />
            Reemplazar Archivo DICOM
          </DialogTitle>
          <DialogDescription>
            Sube un nuevo archivo DICOM para reemplazar el existente.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4 mt-4">
          <div className="border-2 border-dashed border-[var(--border)] rounded-lg p-6 text-center">
            <input
              type="file"
              accept=".dcm,.zip"
              onChange={handleFileChange}
              className="hidden"
              id="dicom-file"
            />
            <label htmlFor="dicom-file" className="cursor-pointer flex flex-col items-center gap-2">
              <Upload className="h-8 w-8 text-[var(--foreground-muted)]" />
              <span className="text-sm text-[var(--foreground-muted)]">
                {file ? file.name : 'Seleccionar archivo DICOM o ZIP'}
              </span>
            </label>
          </div>

          <div className="alert-warning rounded-md p-3">
            <div className="flex items-start gap-2">
              <AlertTriangle className="h-5 w-5 flex-shrink-0 mt-0.5" />
              <p className="text-sm">
                <strong>Advertencia:</strong> Esta acción reemplazará el archivo DICOM actual.
              </p>
            </div>
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancelar
            </Button>
            <Button type="submit" disabled={!file || isUploading}>
              {isUploading ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                  Subiendo...
                </>
              ) : (
                <>
                  <Upload className="h-4 w-4 mr-2" />
                  Reemplazar
                </>
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}

// =============================================================================
// Main Component
// =============================================================================

export function StudyDetailPage() {
  const navigate = useNavigate()
  const { user } = useAuth()
  const { id } = useParams<{ id: string }>()

  // Modals state
  const [showEditModal, setShowEditModal] = useState(false)
  const [showReplaceDicomModal, setShowReplaceDicomModal] = useState(false)
  const [showAssignModal, setShowAssignModal] = useState(false)

  // History pagination
  const [historyPage, setHistoryPage] = useState(0)
  const HISTORY_PER_PAGE = 6

  // Delete confirmation state
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)

  // Queries
  const { data: study, isLoading, isError, error, refetch } = useStudy(id)
  const { data: patient } = usePatient(study?.patientId)
  const descarga = useDescargarEstudio()
  const { data: worklistItem } = useWorklistItemByStudy(id)
  const { data: historyData, isLoading: historyLoading } = useResourceHistory('study', id)
  const deleteMutation = useDeleteStudy()

  // Permissions
  const canEdit = user?.role === USER_ROLES.ADMIN || user?.role === USER_ROLES.SUPER_ADMIN
  const canAssign = user?.role === USER_ROLES.ADMIN || user?.role === USER_ROLES.SUPER_ADMIN || user?.role === USER_ROLES.TECHNICIAN
  const canDelete = user?.role === USER_ROLES.ADMIN || user?.role === USER_ROLES.SUPER_ADMIN

  const handleDelete = () => {
    setDeleteDialogOpen(true)
  }

  const confirmDelete = () => {
    deleteMutation.mutate(id!, {
      onSuccess: () => {
        toast.success('Estudio eliminado correctamente')
        setDeleteDialogOpen(false)
        navigate('/studies')
      },
      onError: () => toast.error('Error al eliminar el estudio'),
    })
  }

  const handleDownload = () => {
    if (!id) return
    descarga.mutate(id, {
      onError: () =>
        toast.error('No se pudo descargar el estudio. Intentelo de nuevo.'),
    })
  }

  if (isLoading) return <LoadingState />
  if (isError) return <ErrorState message={error?.message || 'Error desconocido'} onRetry={refetch} />
  if (!study) return <ErrorState message="Estudio no encontrado" onRetry={refetch} />

  const statusVariant = STUDY_STATUS_COLORS[study.status] || 'default'
  const isReturned = worklistItem?.status === 'RETURNED'

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="sm" onClick={() => navigate(-1)}>
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <div>
            <div className="flex items-center gap-3">
              <h1 className="text-2xl font-bold text-[var(--foreground)]">
                {study.studyDescription || 'Estudio sin descripción'}
              </h1>
              <Badge variant={statusVariant}>
                {STUDY_STATUS_LABELS[study.status]}
              </Badge>
              {isReturned && (
                <Badge variant="warning">
                  {WORKLIST_STATUS_LABELS.RETURNED}
                </Badge>
              )}
            </div>
            <p className="text-[var(--foreground-muted)]">
              {study.accessionNumber || `UID: ${study.studyInstanceUID.slice(0, 30)}...`}
            </p>
          </div>
        </div>
        <div className="flex gap-2 flex-wrap">
          {canAssign && study.status !== 'COMPLETED' && (
            <Button variant="outline" onClick={() => setShowAssignModal(true)}>
              <UserPlus className="h-4 w-4 mr-2" />
              {/* Reasignar solo si ya hay radiologo: el item de lista de
                  trabajo existe desde que el estudio se carga. */}
              {worklistItem?.assignedTo ? 'Reasignar' : 'Asignar'}
            </Button>
          )}
          <Link to={`/viewer/${id}`}>
            <Button>
              <Play className="h-4 w-4 mr-2" />
              Abrir Visor
            </Button>
          </Link>
          <Button variant="outline" onClick={handleDownload} disabled={descarga.isPending}>
            <Download className="h-4 w-4 mr-2" />
            {descarga.isPending ? 'Preparando...' : 'Descargar'}
          </Button>
          {canDelete && (
            <Button
              variant="outline"
              onClick={handleDelete}
              disabled={deleteMutation.isPending}
              className="text-[var(--destructive)] border-[var(--destructive)]/30 hover:bg-[var(--destructive)]/10"
            >
              {deleteMutation.isPending ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Trash2 className="h-4 w-4" />
              )}
            </Button>
          )}
        </div>
      </div>

      {/* Return Alert */}
      {isReturned && worklistItem && (
        <Card className="alert-warning border-0">
          <CardContent className="p-4">
            <div className="flex items-start gap-4 flex-wrap">
              <div className="p-3 rounded-lg bg-[var(--color-warning)]/20">
                <Undo2 className="h-6 w-6" />
              </div>
              <div className="flex-1 min-w-[200px]">
                <h3 className="font-semibold">Estudio Devuelto</h3>
                <p className="text-sm mt-1">
                  Este estudio fue devuelto y requiere corrección antes de ser reasignado.
                </p>
                {worklistItem.returnReason && (
                  <div className="mt-3 p-3 bg-[var(--background)]/50 rounded-md">
                    <p className="text-sm font-medium">Motivo de devolución:</p>
                    <p className="text-sm mt-1">{worklistItem.returnReason}</p>
                  </div>
                )}
                {worklistItem.returnedAt && (
                  <p className="text-xs opacity-80 mt-2">
                    Devuelto el: {formatDateTime(worklistItem.returnedAt)}
                  </p>
                )}
              </div>
              <div className="flex gap-2 flex-wrap">
                {canEdit && (
                  <Link to={`/studies/${id}/amend`}>
                    <Button size="sm">
                      <Wrench className="h-4 w-4 mr-1" />
                      Enmendar
                    </Button>
                  </Link>
                )}
                {canEdit && (
                  <Button size="sm" variant="outline" onClick={() => setShowEditModal(true)}>
                    <Edit className="h-4 w-4 mr-1" />
                    Editar
                  </Button>
                )}
                {canEdit && (
                  <Button size="sm" variant="outline" onClick={() => setShowReplaceDicomModal(true)}>
                    <Upload className="h-4 w-4 mr-1" />
                    Reemplazar
                  </Button>
                )}
                {canAssign && study.status !== 'COMPLETED' && (
                  <Button size="sm" onClick={() => setShowAssignModal(true)}>
                    <UserPlus className="h-4 w-4 mr-1" />
                    Reasignar
                  </Button>
                )}
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Main Content - Modern Grid Layout */}
      <div className="grid gap-4 lg:grid-cols-4">
        {/* Study Info - Spans 2 columns */}
        <Card className="lg:col-span-2">
          <CardHeader className="flex flex-row items-center justify-between pb-3">
            <CardTitle className="flex items-center gap-2">
              <FileImage className="h-5 w-5" />
              Información del Estudio
            </CardTitle>
            {canEdit && (
              <Button variant="outline" size="sm" onClick={() => setShowEditModal(true)}>
                <Edit className="h-4 w-4 mr-1" />
                Editar
              </Button>
            )}
          </CardHeader>
          <CardContent>
            <div className="grid gap-4 grid-cols-2 lg:grid-cols-3">
              <InfoItem
                icon={<Activity className="h-4 w-4" />}
                label="Modalidad"
                value={MODALITY_LABELS[study.modality as Modality] || study.modality}
              />
              <InfoItem
                icon={<Calendar className="h-4 w-4" />}
                label="Fecha"
                value={formatStudyDate(study.studyDate)}
              />
              <InfoItem
                icon={<Clock className="h-4 w-4" />}
                label="Hora"
                value={formatStudyTime(study.studyTime)}
              />
              <InfoItem
                icon={<FileImage className="h-4 w-4" />}
                label="Series / Imágenes"
                value={`${study.numberOfSeries} / ${study.numberOfInstances}`}
              />
              <InfoItem
                icon={<HardDrive className="h-4 w-4" />}
                label="Tamaño"
                value={formatFileSize(study.storageSizeBytes)}
              />
              <InfoItem
                icon={<User className="h-4 w-4" />}
                label="Parte del Cuerpo"
                value={study.bodyPartExamined}
              />
            </div>
          </CardContent>
        </Card>

        {/* Patient Card */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-3">
            <CardTitle className="flex items-center gap-2">
              <User className="h-5 w-5" />
              Paciente
            </CardTitle>
            {patient && (
              <Link to={`/patients/${patient.id}`}>
                <Button variant="ghost" size="sm" className="h-7 px-2">
                  <Eye className="h-4 w-4" />
                </Button>
              </Link>
            )}
          </CardHeader>
          <CardContent>
            {patient ? (
              <div className="space-y-2">
                <div>
                  <p className="text-xs text-[var(--foreground-muted)]">Nombre</p>
                  <p className="font-medium text-sm">{patient.firstName} {patient.lastName}</p>
                </div>
                <div>
                  <p className="text-xs text-[var(--foreground-muted)]">Documento</p>
                  <p className="font-medium text-sm">{patient.document.type} {patient.document.number}</p>
                </div>
              </div>
            ) : (
              <p className="text-sm text-[var(--foreground-muted)]">Sin paciente</p>
            )}
          </CardContent>
        </Card>

        {/* Status Card */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle>Estado</CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            <div className="flex justify-between items-center">
              <span className="text-xs text-[var(--foreground-muted)]">Estudio</span>
              <Badge variant={statusVariant} className="text-xs">
                {STUDY_STATUS_LABELS[study.status]}
              </Badge>
            </div>
            {worklistItem && (
              <>
                <div className="flex justify-between items-center">
                  <span className="text-xs text-[var(--foreground-muted)]">Worklist</span>
                  <Badge variant={isReturned ? 'warning' : 'default'} className="text-xs">
                    {WORKLIST_STATUS_LABELS[worklistItem.status as keyof typeof WORKLIST_STATUS_LABELS] || worklistItem.status}
                  </Badge>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-xs text-[var(--foreground-muted)]">Asignado a</span>
                  <span className="text-xs font-medium">
                    {(worklistItem as { assignedToName?: string }).assignedToName || 'Sin asignar'}
                  </span>
                </div>
              </>
            )}
            <div className="pt-2 border-t border-[var(--border)] space-y-1">
              <div className="flex justify-between text-xs">
                <span className="text-[var(--foreground-muted)]">Creado</span>
                <span>{formatDateTime(study.createdAt)}</span>
              </div>
              <div className="flex justify-between text-xs">
                <span className="text-[var(--foreground-muted)]">Actualizado</span>
                <span>{formatDateTime(study.updatedAt)}</span>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Medical Info */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2">
              <Building2 className="h-5 w-5" />
              Información Médica
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              <InfoItem
                icon={<User className="h-4 w-4" />}
                label="Médico Remitente"
                value={study.referringPhysician}
              />
              <InfoItem
                icon={<User className="h-4 w-4" />}
                label="Médico Ejecutante"
                value={study.performingPhysician}
              />
            </div>
          </CardContent>
        </Card>

        {/* Actions Card */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle>Acciones Rápidas</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 gap-2">
              <Link to={`/viewer/${id}`}>
                <Button variant="outline" size="sm" className="w-full">
                  <Play className="h-4 w-4 mr-1" />
                  Visor
                </Button>
              </Link>
              <Button
                variant="outline"
                size="sm"
                onClick={handleDownload}
                disabled={descarga.isPending}
              >
                <Download className="h-4 w-4 mr-1" />
                {descarga.isPending ? 'Preparando...' : 'Descargar'}
              </Button>
              {!study.reportId && (
                <Link to={`/reports/new?studyId=${id}`} className="col-span-2">
                  <Button variant="outline" size="sm" className="w-full">
                    <FileText className="h-4 w-4 mr-1" />
                    Crear Reporte
                  </Button>
                </Link>
              )}
              {study.reportId && (
                <Link to={`/reports/${study.reportId}`} className="col-span-2">
                  <Button variant="outline" size="sm" className="w-full">
                    <FileText className="h-4 w-4 mr-1" />
                    Ver Reporte
                  </Button>
                </Link>
              )}
              {canEdit && (
                <>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setShowEditModal(true)}
                  >
                    <Edit className="h-4 w-4 mr-1" />
                    Editar
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setShowReplaceDicomModal(true)}
                  >
                    <Upload className="h-4 w-4 mr-1" />
                    DICOM
                  </Button>
                </>
              )}
            </div>
          </CardContent>
        </Card>

        {/* History Card - Spans 2 columns */}
        <Card className="lg:col-span-2">
          <CardHeader className="pb-3 flex flex-row items-center justify-between">
            <CardTitle className="flex items-center gap-2">
              <History className="h-5 w-5" />
              Historial de Actividad
            </CardTitle>
            {historyData?.history && historyData.history.length > HISTORY_PER_PAGE && (
              <div className="flex items-center gap-1">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setHistoryPage((p) => Math.max(0, p - 1))}
                  disabled={historyPage === 0}
                  className="h-7 w-7 p-0"
                >
                  ←
                </Button>
                <span className="text-xs text-[var(--foreground-muted)] min-w-[40px] text-center">
                  {historyPage + 1}/{Math.ceil(historyData.history.length / HISTORY_PER_PAGE)}
                </span>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setHistoryPage((p) => p + 1)}
                  disabled={(historyPage + 1) * HISTORY_PER_PAGE >= historyData.history.length}
                  className="h-7 w-7 p-0"
                >
                  →
                </Button>
              </div>
            )}
          </CardHeader>
          <CardContent>
            {historyLoading ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="h-5 w-5 animate-spin" />
              </div>
            ) : !historyData?.history?.length ? (
              <p className="text-sm text-[var(--foreground-muted)] text-center py-8">
                Sin historial de actividad
              </p>
            ) : (
              <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
                {historyData.history
                  .slice(historyPage * HISTORY_PER_PAGE, (historyPage + 1) * HISTORY_PER_PAGE)
                  .map((log) => (
                    <div
                      key={log.id}
                      className="flex items-start gap-2 p-3 rounded-lg border border-[var(--border)] bg-[var(--secondary)]/20"
                    >
                      <div className={`p-1.5 rounded-full bg-[var(--secondary)] flex-shrink-0 ${getActionColor(log.action)}`}>
                        {log.action === 'CREATE' && <CheckCircle className="h-3.5 w-3.5" />}
                        {log.action === 'UPDATE' && <Edit className="h-3.5 w-3.5" />}
                        {log.action === 'READ' && <Eye className="h-3.5 w-3.5" />}
                        {log.action === 'DELETE' && <XCircle className="h-3.5 w-3.5" />}
                        {log.action === 'DOWNLOAD' && <Download className="h-3.5 w-3.5" />}
                        {log.action === 'UPLOAD' && <Upload className="h-3.5 w-3.5" />}
                        {log.action === 'ASSIGN' && <UserPlus className="h-3.5 w-3.5" />}
                        {log.action === 'RETURN' && <Undo2 className="h-3.5 w-3.5" />}
                        {!['CREATE', 'UPDATE', 'READ', 'DELETE', 'DOWNLOAD', 'UPLOAD', 'ASSIGN', 'RETURN'].includes(log.action) && (
                          <History className="h-3.5 w-3.5" />
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-[var(--foreground)]">
                          {AUDIT_ACTION_LABELS[log.action] || log.action}
                        </p>
                        <p className="text-xs text-[var(--foreground-muted)] truncate">
                          {log.user?.email || 'Sistema'}
                        </p>
                        <p className="text-xs text-[var(--foreground-muted)]">
                          {formatDateTime(log.timestamp)}
                        </p>
                        {log.details && typeof log.details === 'object' && 'reason' in log.details && (
                          <p className="text-xs text-orange-600 dark:text-orange-400 mt-1 truncate">
                            {String(log.details.reason)}
                          </p>
                        )}
                      </div>
                    </div>
                  ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Modals */}
      {study && (
        <EditStudyModal
          open={showEditModal}
          onOpenChange={setShowEditModal}
          study={{
            id: study.id,
            studyDescription: study.studyDescription,
            referringPhysician: study.referringPhysician,
            performingPhysician: study.performingPhysician,
            bodyPartExamined: study.bodyPartExamined,
          }}
          onSuccess={() => refetch()}
        />
      )}

      <ReplaceDicomModal
        open={showReplaceDicomModal}
        onOpenChange={setShowReplaceDicomModal}
        studyId={id!}
        onSuccess={() => refetch()}
      />

      <AssignStudyModal
        open={showAssignModal}
        onOpenChange={setShowAssignModal}
        studyId={id ?? null}
        studyDescription={study?.studyDescription || undefined}
        onSuccess={() => refetch()}
      />

      {/* Delete Confirmation Dialog */}
      <ConfirmDialog
        open={deleteDialogOpen}
        onOpenChange={setDeleteDialogOpen}
        title="Eliminar estudio"
        description="¿Estás seguro de que deseas eliminar este estudio? Esta acción no se puede deshacer."
        confirmLabel="Eliminar"
        cancelLabel="Cancelar"
        variant="danger"
        onConfirm={confirmDelete}
        isLoading={deleteMutation.isPending}
      />
    </div>
  )
}
